import { Grid2 } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { TableTaskStatus } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import ViewTab from '@components/ViewTab';

import { useGetAllCuratorTasksForPatient } from '@hooks/api/curator-task';

import { TaskPriorityMap } from '@utils/curator-task';
import { formatDate, formatName } from '@utils/format';

import type { CuratorTaskModel } from 'generated/types';

const messages = defineMessages({
  noTasks: {
    id: 'clientProfile.noTasks',
    defaultMessage: 'No Tasks',
  },
  outstandingTasks: {
    id: 'clientProfile.outstandingTasks',
    defaultMessage: 'Outstanding Tasks',
  },
  completeTasks: {
    id: 'clientProfile.completeTasks',
    defaultMessage: 'Complete Tasks',
  },
});

const TaskList: React.FC<{
  vitall_patient_id?: string | null;
}> = ({ vitall_patient_id }) => {
  const { formatMessage } = useIntl();

  const tabs = [
    { id: 'outstandingTasks', value: formatMessage(messages.outstandingTasks) },
    { id: 'completeTasks', value: formatMessage(messages.completeTasks) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const { data: tasksData, loading: tasksLoading } =
    useGetAllCuratorTasksForPatient({ vitall_patient_id });
  const loading = tasksLoading;

  const tasks = tasksData?.data;
  const completeTasks = tasks?.filter(({ status }) => status);
  const incompleteTasks = tasks?.filter(({ status }) => !status);

  const taskData = (
    activeTab.id === 'outstandingTasks' ? incompleteTasks : completeTasks
  )?.map((task) => {
    const {
      curator_task_id,
      description,
      due_date,
      name,
      priority,
      status,
      user,
    } = task;

    const curatorName = `${
      user ? formatName(user.first_name, user.last_name) : '-'
    }`;

    return {
      id: curator_task_id,
      name,
      assignee: curatorName,
      description,
      dueDate: formatDate({ date: due_date }),
      priority,
      status,
      task,
    };
  });

  if (!vitall_patient_id) {
    return null;
  }

  return (
    <ContentLoader loading={loading}>
      <Grid2 marginBottom={4}>
        <MuiDataTable
          columns={[
            {
              name: 'task',
              label: 'Status',
              options: {
                customHeadLabelRender: () => null,
                customBodyRender: (task: CuratorTaskModel) => (
                  <TableTaskStatus task={task} />
                ),
              },
            },
            {
              name: 'name',
              className: 'small',
              label: 'Task Name',
              options: {
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'description',
              className: 'small',
              label: 'Description',
              options: {
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'dueDate',
              className: 'date',
              label: 'Due Date',
              options: {
                filter: true,
                sort: true,
              },
            },

            {
              name: 'priority',
              className: 'small',
              label: 'Priority',
              options: {
                filter: true,
                sort: true,
                customBodyRender: (value: number) =>
                  TaskPriorityMap[value].text,
              },
            },
            {
              name: 'assignee',
              className: 'blue-hover',
              label: 'Assignee',
              options: {
                filter: true,
                sort: true,
                searchable: true,
              },
            },
          ]}
          data={taskData ?? []}
          options={{
            search: true,
            sort: true,
            filter: true,
            textLabels: {
              body: { noMatch: formatMessage(messages.noTasks) },
            },
          }}
          title={
            <ViewTab
              changeTab={setActiveTab}
              selected={activeTab}
              tabArray={tabs}
            />
          }
        />
      </Grid2>
    </ContentLoader>
  );
};

export default TaskList;
